import AccordionCustom from "@components/Accordion/AccordionCustom";
import ImageGrid from "@components/ImageGrid";
import { CardDataDetailsProps } from "@components/LandingPageComponents/CardDataDetails";
import FeatureCardSection from "@components/LandingPageComponents/CardSection";
import OdooHeader from "@components/LandingPageComponents/OdooHeader";
import TestimonialSection from "@components/LandingPageComponents/TestimonialSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import NewLayout from "@components/new-ui/Layout";
import YellowCTAhref from "@components/yellowCTAhref";
import dynamic from "next/dynamic";
import Head from "next/head";
import goireland from "public/goireland.png";
import React from "react";

const cardDataCollection: CardDataDetailsProps[] = [
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-1.svg",
        title: "Redefine Travel Experiences",
        description:
            "Whenever a contact inquires about a travel package send a series of WhatsApp nudges to users about everything from personalised itineraries to custom quotations.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/personalized-card.png",
        title: "Improve E-Commerce Conversions",
        description:
            "Is abandoned cart a never-ending bottleneck? Whenever users miss out on completing their transactions nudge them with coupons and tailor-made offers to win them back.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-3.svg",
        title: "Scale Your Ed-Tech Business",
        description:
            "Qualify and nurture leads on WhatsApp, boost student-teacher engagement, and acquire students at scale and with confidence with the Gallabox and MoEngage integration.",
    },
    {
        img: "https://assets.gallabox.com/gb-home/zoho-card-4.svg",
        title: "Enrich the Healthcare Experience",
        description:
            "Improve the quality of care and earn patients' trust with WhatsApp Automation. Answer repetitive questions and send automated alerts on time to make a lasting impression.",
    },
];

const CardDataCarousal = dynamic(() => import("@components/LandingPageComponents/CardDataCarousal"), {
    ssr: false,
});

const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
        {
            "@type": "Question",
            name: "How do I integrate WhatsApp with MoEngage?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "You can either connect WhatsApp and MoEngage with the help of data integration platforms like Zapier or use dedicated WhatsApp Automation Solutions like Gallabox. While using a connecting platform would require a ton of manual time and effort, investing in WhatsApp Chatbot tools like Gallabox makes this process easy with pre-built native integrations.",
            },
        },

        {
            "@type": "Question",
            name: "Why integrate MoEngage with WhatsApp Business?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "A seamless integration between WhatsApp and MoEngage allows businesses to send rich multimedia messages, which adds interactivity and makes customer engagement affordable. It also allows for instant, two-way conversations, helping leverage WhatsApp as a major channel to have customer conversations that matter and build lasting relationships.",
            },
        },
        {
            "@type": "Question",
            name: " How can I improve customer engagement with WhatsApp and MoEngage?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "By integrating MoEngage with their WhatsApp Business accounts, businesses can effectively track customer behaviour. With the power of ready-to-use templates and AI-powered chatbots offered by WhatsApp Automation Platforms like Gallabox, MoEngage can deliver personalised brand experiences that help improve customer reach and retention.",
            },
        },
        {
            "@type": "Question",
            name: "How do I connect MoEngage to WhatsApp using Gallabox?",
            acceptedAnswer: {
                "@type": "Answer",
                text: "Connecting MoEngage with WhatsApp Business API is swift and easy with Gallabox connectors. You can initiate the integration process either from Gallabox or through the MoEngage Platform. Here’s how you configure Gallabox as a sender from your MoEngage Dashboard: 1.Log into your MoEngage account,2.Find the Gallabox Connector in the MoEngage marketplace,3.Install the app, enter your credentials, and configure the sender details.",
            },
        },
    ],
};

const Moengage: React.FC = () => {
    return (
        <NewLayout
            title="MoEngage WhatsApp Integration for Marketing | Gallabox"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/moengage/moengage-og.png" }}
            canonical="https://gallabox.com/whatsapp-integrations/moengage"
            description="Boost retention and engagement with MoEngage WhatsApp integration by Gallabox. Automate campaigns and notifications."
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
            </Head>
            <div className="bg-white">
                <OdooHeader
                    pageTitle="WhatsApp MoEngage Integration"
                    mainTitle="MoEngage WhatsApp Integration for Better Marketing Campaigns"
                    description="Ensure a seamless end-to-end lead, qualification, nurturing and engagement from initial contact to deal closure or purchase with personalised follow-ups."
                    buttonText="Sign Up Today"
                    imageUrl="https://assets.gallabox.com/gb-home/moengage/header.png"
                />

                <div className="lg:max-w-7xl mx-auto">
                    <h3 className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</h3>
                    <ClientLogos />
                </div>

                <CardDataCarousal cardDataCollection={cardDataCollection} />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/moengage/section-1.png"
                    title="Go Beyond Manual Alerts with the MoEngage Integration"
                    description="Keep your contacts engaged throughout their lifecycle with triggers based on custom actions ranging from cart abandonment to successful appointment booking. Stay on top of your contact’s mind and encourage them to take action."
                    isImageRightSide
                />

                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/moengage/section-4.png"
                    title="Make Customer and Prospect Engagement a Top Priority"
                    description="Reach a large audience base through marketing campaigns and Click-to-WhatsApp Ads to support a series of business goals like new conversions, guided onboarding, customer activation, cross- and up-selling, retention, and more."
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/moengage/section-3.png"
                    title="Connect Seamlessly with Your Tech Stack to Improve Effectiveness"
                    description="Stop juggling between different platforms to understand the results of your campaigns. Integrate MoEngage with Gallabox to get a single source of truth, measure campaign performance effectively, and deliver exceptional customer experience."
                    isImageRightSide
                />
                <FeatureCardSection
                    imageURl="https://assets.gallabox.com/gb-home/moengage/section-2.png"
                    title="Maximise Customer Engagement with Targeted, Personalised Replies"
                    description="Use an eloquent assistant like ChatGPT to frame engaging replies, keep up with SLA, and improve customer satisfaction. Shorten or fluff up content and talk to customers and prospects in their own language to make canned responses sound candid."
                />

                <YellowCTAhref
                    buttonText="Get Started Today"
                    customText={<h3 className="text-2xl md:text-3xl font-bold text-yellow-800">Sync MoEngage with WhatsApp and Watch Your Engagement Rates Skyrocket</h3>}
                />

                <TestimonialSection
                    TestimonialSectionPropsData={[
                        {
                            companyImg: goireland,
                            headerTitle: "Gallabox’s WhatsApp Widget reduced FTR  from 15-30 minutes to less than a minute, boosting engagement and conversion rates.",
                            avatarImg: "https://assets.gallabox.com/gb-home/go-ireland/goireland-owner.png",
                            nameText: "Ilaya Bharathi",
                            professionText: "Founder & CEO, GoIreland",
                        },
                    ]}
                />

                <ImageGrid />

                <section className="max-w-6xl py-4 mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-center pb-6">
                        <p className="lg:text-4xl text-3xl text-center font-bold text-gray-700">Frequently asked questions</p>
                        <h3 className="text-gray-500 text-center lg:text-2xl text-lg font-normal ">Everything you need to know about MoEngage-Gallabox integration</h3>
                    </div>
                    <AccordionCustom
                        title="MoEngage-Gallabox integration"
                        items={[
                            {
                                question: "How do I integrate WhatsApp with MoEngage?",
                                answer: "You can either connect WhatsApp and MoEngage with the help of data integration platforms like Zapier or use dedicated WhatsApp Automation Solutions like Gallabox. While using a connecting platform would require a ton of manual time and effort, investing in WhatsApp Chatbot tools like Gallabox makes this process easy with pre-built native integrations.",
                            },
                            {
                                question: "Why integrate MoEngage with WhatsApp Business?",
                                answer: "A seamless integration between WhatsApp and MoEngage allows businesses to send rich multimedia messages, which adds interactivity and makes customer engagement affordable. It also allows for instant, two-way conversations, helping leverage WhatsApp as a major channel to have customer conversations that matter and build lasting relationships.",
                            },
                            {
                                question: "How can I improve customer engagement with WhatsApp and MoEngage?",
                                answer: "By integrating MoEngage with their WhatsApp Business accounts, businesses can effectively track customer behaviour. With the power of ready-to-use templates and AI-powered chatbots offered by WhatsApp Automation Platforms like Gallabox, MoEngage can deliver personalised brand experiences that help improve customer reach and retention.",
                            },
                            {
                                question: "How do I connect MoEngage to WhatsApp using Gallabox?",
                                answer: (
                                    <>
                                        <span>
                                            {
                                                "Connecting MoEngage with WhatsApp Business API is swift and easy with Gallabox connectors. You can initiate the integration process either from Gallabox or through the MoEngage Platform. "
                                            }
                                        </span>
                                        <span>{"Here’s how you configure Gallabox as a sender from your MoEngage Dashboard:"}</span>

                                        <ol className="pl-1">
                                            <li>{"Log into your MoEngage account."}</li>
                                            <li>{"Find the Gallabox Connector in the MoEngage marketplace."}</li>
                                            <li>{"Install the app, enter your credentials, and configure the sender details."}</li>
                                        </ol>
                                    </>
                                ),
                            },
                        ]}
                    />
                </section>
            </div>
        </NewLayout>
    );
};

export default Moengage;
